/**
 * Client-side Cache Registry
 *
 * Central registry of all browser-side caches (localStorage, sessionStorage, IndexedDB)
 * that must be cleared on logout or organization switch to prevent stale/cross-account data.
 */

import { logger } from '@/lib/logger';

// ============================================================================
// localStorage keys to clear on logout
// ============================================================================

const STATIC_KEYS = [
  // Campaign state
  'content-studio-last-campaign',
  'content-studio-draft',

  // CRM iframe caches
  'crm_session_v1',
  'crm_schema_ready_v1',
  'crm_fast_nav_v1',
  'crm_skip_cache_clear_v1',
  'maiteams_prefetched_metadata_v1',

  // Feature-specific caches
  'activeVideoGenerations',
  'voiceSettings',
  'lead-finder-search-results',

  // Stale key from the removed viewing-as feature; the org provider also
  // deletes it on load.
  'viewing-as-user',
] as const;

// Pattern-based keys (matched via regex)
const PATTERN_KEYS = [
  /^crm-views-/,
  /^crm-activeView-/,
  /^crm-recentSearches-/,
  /^crm-columnColors-/,
  /^crm-columnWidths-/,
  // Workspace 2027 writes its per-browser view preferences in the colon form
  // (`crm:density`, `crm:group-by:<object>`) — machine-local choices that must
  // not survive into the next account signed in on this browser.
  /^crm:/,
  /table-field-visibility$/,
  /^brand_assets_/,
  /^notifications-user-/,
  // RealtimeProvider's persisted feeds — account-scoped content that must not
  // survive into the next login (and, uncapped historically, grew unbounded).
  /^realtime-(messages|status-updates)-v2$/,
] as const;

// ============================================================================
// Public API
// ============================================================================

/**
 * Clears all known client-side caches from localStorage and sessionStorage.
 * Call on logout (SIGNED_OUT) to prevent cross-account data leakage.
 */
export function clearAllClientCaches(): void {
  if (typeof window === 'undefined') return;

  try {
    // Clear static keys
    for (const key of STATIC_KEYS) {
      localStorage.removeItem(key);
    }

    // Clear pattern-matched keys
    const allKeys = Object.keys(localStorage);
    for (const key of allKeys) {
      for (const pattern of PATTERN_KEYS) {
        if (pattern.test(key)) {
          localStorage.removeItem(key);
          break;
        }
      }
    }

    // Clear all sessionStorage (contains generated content, enriched companies, etc.)
    sessionStorage.clear();

    logger.debug('[CacheRegistry] Cleared all client-side caches');
  } catch (error) {
    logger.error('[CacheRegistry] Error clearing client caches:', error);
  }
}

/**
 * Clears CRM-related IndexedDB databases (Apollo GraphQL cache, Twenty CRM).
 * Returns a promise since IndexedDB operations are async.
 */
export async function clearCrmIndexedDB(): Promise<void> {
  if (typeof window === 'undefined' || !window.indexedDB) return;

  try {
    const databases = await window.indexedDB.databases();
    const crmPatterns = ['apollo', 'twenty', 'graphql'];

    for (const db of databases) {
      if (db.name && crmPatterns.some(p => db.name!.toLowerCase().includes(p))) {
        window.indexedDB.deleteDatabase(db.name);
        logger.debug(`[CacheRegistry] Deleted IndexedDB: ${db.name}`);
      }
    }
  } catch (error) {
    // indexedDB.databases() may not be available in all browsers
    logger.debug('[CacheRegistry] Could not enumerate IndexedDB databases:', error);
  }
}
